// 12.06.25 14:40 - Updated Dashboard.js to include Drawing Practice module
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getFirestore, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import './Dashboard.css';
// 26.11.25 AEST - Import filename cleaner utility
import { cleanFilename } from '../utils/filenameUtils';

const Dashboard = ({ toggleDrawer }) => {
  const { currentUser } = useAuth();
  
  // 05.09.25 18:05 AEST - Debug current user
  console.log('Current user:', currentUser);
  console.log('User email:', currentUser?.email);
  console.log('User ID:', currentUser?.uid);
  
  const [stats, setStats] = useState({
    totalFiles: 0,
    recentFiles: [],
    upcomingReminders: 0,
    recentReminders: [],
    recentEmails: [],
    recentGoals: []
  });
  const [loading, setLoading] = useState(true);

  // 12.06.25 14:40 - Fetch dashboard statistics
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!currentUser) return;

      try {
        const db = getFirestore();

        // Fetch recent files
        const filesQuery = query(
          collection(db, 'Users', currentUser.email, 'Files'),
          orderBy('uploadedAt', 'desc'),
          limit(5)
        );
        const filesSnapshot = await getDocs(filesQuery);
        const recentFiles = filesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

       // 05.09.25 20:25 AEST - Simplified query to avoid composite index requirement

 // 09.09.25 11:35 AEST - Fixed syntax error with minimal change
 const notificationsQuery = query(
  collection(db, 'Users', currentUser.email, 'Notifications'),
  where('type', '==', 'reminder'),
  limit(10)
);

const remindersSnapshot = await getDocs(notificationsQuery);
const recentReminders = remindersSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Count total files
        const allFilesQuery = query(collection(db, 'Users', currentUser.email, 'Files'));
        const allFilesSnapshot = await getDocs(allFilesQuery);

// Fetch recent emails
const emailsQuery = query(
  collection(db, 'Users', currentUser.email, 'Emails'),
  orderBy('receivedAt', 'desc'),
  limit(3)
);
const emailsSnapshot = await getDocs(emailsQuery);
const recentEmails = emailsSnapshot.docs.map(doc => ({
  id: doc.id,
  ...doc.data()
}));

// Fetch recent goals
const goalsQuery = query(
  collection(db, 'Users', currentUser.email, 'Goals'),
  limit(3)
);
const goalsSnapshot = await getDocs(goalsQuery);
const recentGoals = goalsSnapshot.docs.map(doc => ({
  id: doc.id,
  ...doc.data()
}));

console.log('Dashboard data loaded:', {
  recentFiles: recentFiles.length,
  recentReminders: recentReminders.length,
  recentEmails: recentEmails.length,
  recentGoals: recentGoals.length
});

setStats({
  totalFiles: allFilesSnapshot.size,
  recentFiles,
  upcomingReminders: remindersSnapshot.size,
  recentReminders,
  recentEmails,
  recentGoals
});
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [currentUser]);

  // 12.06.25 14:40 - Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown date';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch (err) {
      return 'Invalid date';
    }
  };

  return (
    <div className="dashboard-container">
      {/* 12.06.25 14:40 - Dashboard header */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Welcome back!</h1>
          <p>Ready to COOC?! Control, Own, Organise and Connect?!</p>
        </div>
        
        {/* 12.06.25 14:40 - Quick stats */}
        <div className="quick-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.totalFiles}</span>
            <span className="stat-label">Total Files</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.upcomingReminders}</span>
            <span className="stat-label">Reminders</span>
          </div>
        </div>
      </div>

     
{/* 11.09.25 16:15 AEST - 6-module grid: Files|Reminders, Emails|Goals, Noticeboard|Drawing */}
<div className="dashboard-modules">
        {/* Files Module */}
        <div className="module-card">
          <div className="module-header">
            <h3>📁 Recent Files</h3>
            <Link to="/files" className="view-all-btn">View All</Link>
          </div>
          <div className="module-content">
            {loading ? (
              <div className="loading-text">Loading files...</div>
            ) : stats.recentFiles.length > 0 ? (
              <div className="files-list">
               {stats.recentFiles.map(file => (
  <div 
    key={file.id} 
    className="file-item clickable-file" 
    onClick={() => {
      console.log('Dashboard file clicked:', file);
      if (file.downloadURL) {
        window.open(file.downloadURL, '_blank');
      } else {
        alert('No download URL available for: ' + file.fileName);
      }
    }}
    style={{ cursor: 'pointer' }}
  >
<div className="file-info">
      {/* 26.11.25 AEST - Display clean filename */}
      <span className="file-name">{cleanFilename(file.fileName)}</span>
      <span className="file-category">{file.category || 'Uncategorized'}</span>
    </div>
    <span className="file-date">{formatDate(file.uploadedAt)}</span>
  </div>
))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No files uploaded yet</p>
                <Link to="/upload" className="primary-button">Upload First File</Link>
              </div>
            )}
          </div>
        </div>

        {/* Reminders Module */}
        <div className="module-card">
          <div className="module-header">
            <h3>⏰ Upcoming Reminders</h3>
            <Link to="/reminders" className="view-all-btn">View All</Link>
          </div>
          <div className="module-content">
            {loading ? (
              <div className="loading-text">Loading reminders...</div>
            ) : stats.recentReminders.length > 0 ? (
              <div className="reminders-list">
                {stats.recentReminders.map(reminder => (
                  <div key={reminder.id} className="reminder-item">
<div className="reminder-info">
                      {/* 26.11.25 AEST - Display clean filename */}
                      <span className="reminder-title">{cleanFilename(reminder.title)}</span>
                      <span className="reminder-message">{cleanFilename(reminder.message)}</span>
                    </div>
                    <span className="reminder-date">{formatDate(reminder.dueDate)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p>No upcoming reminders</p>
              </div>
            )}
          </div>
        </div>

{/* 28.11.25 10:10 AEST - Drawing Practice Module - Coming Soon */}
<div className="module-card coming-soon-module">
          <div className="module-header">
            <h3>✏️ Drawing Practice</h3>
            <span className="coming-soon-label">Coming Soon</span>
          </div>
          <div className="module-content">
            <div className="drawing-practice-preview">
              <div className="practice-icon">🎨</div>
              <div className="practice-info">
                <h4>Improve Fine Motor Skills</h4>
                <p>Practice drawing geometric shapes with increasing difficulty</p>
                <div className="practice-features">
                  <span className="feature-tag">Progressive Levels</span>
                  <span className="feature-tag">Real-time Feedback</span>
                  <span className="feature-tag">Skill Tracking</span>
                </div>
              </div>
            </div>
{/* 28.11.25 10:10 AEST - Removed stats, added coming soon message */}
<div className="coming-soon-message">
              <p>🚀 We're working on this feature!</p>
              <p style={{fontSize: '12px', color: '#999'}}>Practice drawing shapes to improve fine motor skills</p>
            </div>
           </div>
        </div>

{/* 28.11.25 10:15 AEST - Noticeboard Module - Coming Soon */}
<div className="module-card coming-soon-module">
          <div className="module-header">
            <h3>💬 Noticeboard</h3>
            <span className="coming-soon-label">Coming Soon</span>
          </div>
          <div className="module-content">
            {/* 28.11.25 10:15 AEST - Coming soon content */}
            <div className="coming-soon-message">
              <p>🚀 Community features launching soon!</p>
              <p style={{fontSize: '12px', color: '#999'}}>Connect with other COOC users</p>
            </div>
          </div>
        </div>

       {/* 11.09.25 16:15 AEST - Email Module (updated for grid position) */}
      {/* Emails Module */}
<div className="module-card">
  <div className="module-header">
    <h3>📧 Recent Emails</h3>
    <Link to="/emails" className="view-all-btn">View All</Link>
  </div>
  <div className="module-content">
  {loading ? (
    <div className="loading-text">Loading emails...</div>
  ) : stats.recentEmails.length > 0 ? (
    <div className="emails-list">
      {stats.recentEmails.map(email => (
        <div key={email.id} className="content-item">
          <div className="email-details">
            <div className="email-from">{email.from}</div>
            <div className="email-subject">{email.subject}</div>
          </div>
          <div className="email-date">{formatDate(email.receivedAt)}</div>
        </div>
      ))}
    </div>
  ) : (
    <div className="empty-state">
      <p>No emails received yet</p>
      <p style={{fontSize: '12px', color: '#999', marginTop: '8px'}}>
        Documents sent to your COOC email will appear here
      </p>
    </div>
  )}
</div>
</div>

       {/* 11.09.25 16:15 AEST - Goals Module (updated for grid position) */}
     {/* Goals Module */}
<div className="module-card">
  <div className="module-header">
    <h3>🎯 NDIS Goals</h3>
    <Link to="/goals" className="view-all-btn">View All</Link>
  </div>
  <div className="module-content">
  {loading ? (
    <div className="loading-text">Loading goals...</div>
  ) : stats.recentGoals.length > 0 ? (
    <div className="goals-list">
      {stats.recentGoals.map(goal => (
        <div key={goal.id} className="content-item">
          <div className="goal-details">
            <div className="goal-title">{goal.title}</div>
            <div className="goal-category">{goal.category}</div>
          </div>
        </div>
      ))}
    </div>
  ) : (
    <div className="empty-state">
      <p>No goals set yet</p>
      <p style={{fontSize: '12px', color: '#999', marginTop: '8px'}}>
        Track your NDIS plan goals and milestones
      </p>
    </div>
  )}
</div>
</div>
      </div>
    </div>
  );
};

export default Dashboard;